import { useMemo } from 'react';

import {
  FilterKind,
  Permission,
  type RadioFilterDescriptor,
  type TableFilterDescriptor,
  type Token,
} from '@fly/shared';

/**
 * Returns filter descriptors for the tokens table (permission, createdBy).
 * Use with TanstackTable filterDescriptors; useFilterDescriptors converts these to TableFilterOption[].
 *
 * Constructs descriptors directly (not via createRadioFilter) to avoid
 * importing @fly/shared-ui from shared-hooks (circular dependency).
 */
export function useTokensFilters(
  data: Token[]
): TableFilterDescriptor<Token>[] {
  const creators = useMemo(() => {
    return Array.from(new Set(data?.map((item) => item.subject))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [data]);

  return useMemo(() => {
    const permissionDescriptor: RadioFilterDescriptor<Token> = {
      kind: FilterKind.RADIO,
      key: 'permission',
      label: [
        'app.dashboard.token_management.columns.permission',
        { defaultValue: 'Permission' },
      ],
      optionsSource: {
        type: 'static',
        options: [
          {
            value: Permission.READ_WRITE,
            label: [
              'app.dashboard.token_management.columns.permission_read_write',
              { defaultValue: 'Read & Write' },
            ],
          },
          {
            value: Permission.READ,
            label: [
              'app.dashboard.token_management.columns.permission_read_only',
              { defaultValue: 'Read Only' },
            ],
          },
        ],
        clientFilterFn: (row, value) => row.scope === value,
      },
    };

    const descriptors: TableFilterDescriptor<Token>[] = [
      permissionDescriptor,
      ...(creators.length > 0
        ? [
            {
              kind: FilterKind.RADIO,
              key: 'createdBy',
              label: [
                'app.dashboard.token_management.columns.created_by',
                { defaultValue: 'Created By' },
              ],
              optionsSource: {
                type: 'static' as const,
                options: creators.map((creator) => ({
                  value: creator,
                  label: [creator, { defaultValue: creator }] as [
                    string,
                    { defaultValue?: string },
                  ],
                })),
                clientFilterFn: (row: Token, value: string) =>
                  row.subject === value,
              },
            } satisfies RadioFilterDescriptor<Token>,
          ]
        : []),
    ];

    return descriptors;
  }, [creators]);
}
